import React from 'react';
import { Box, Dialog, DialogTitle, IconButton, Paper, PaperProps, Stack, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { ResponsiveStyleValue } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import Draggable, { DraggableEventHandler } from 'react-draggable';

export interface DetachablePanelProps {
    open: boolean;
    onClose: () => void;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    headerActions?: React.ReactNode;
    children: React.ReactNode;
    panelId: string;
    width?: ResponsiveStyleValue<string | number>;
    height?: ResponsiveStyleValue<string | number>;
    defaultPosition?: { x: number; y: number };
}

interface PanelStorage {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
}

function storageKey(panelId: string): string {
    return `panel:${panelId}`;
}

function loadPanelStorage(panelId: string): PanelStorage {
    try {
        const raw = localStorage.getItem(storageKey(panelId));
        return raw ? (JSON.parse(raw) as PanelStorage) : {};
    } catch {
        return {};
    }
}

function savePanelStorage(panelId: string, patch: Partial<PanelStorage>): void {
    try {
        const current = loadPanelStorage(panelId);
        localStorage.setItem(storageKey(panelId), JSON.stringify({ ...current, ...patch }));
    } catch {
        // localStorage unavailable — silently ignore
    }
}

const CANCEL_SELECTOR = '[class*="MuiDialogContent-root"], button, [role="slider"]';

let _instanceCounter = 0;

function makeDraggablePaper(
    handleId: string,
    panelId: string,
    paperRef: React.RefObject<HTMLDivElement>,
    onDragStop: DraggableEventHandler,
    defaultPositionFallback?: { x: number; y: number },
): React.FC<PaperProps> {
    const stored = loadPanelStorage(panelId);
    const defaultPosition = stored.x != null && stored.y != null
        ? { x: stored.x, y: stored.y }
        : (defaultPositionFallback ?? { x: 0, y: 0 });

    const DraggablePaperComponent: React.FC<PaperProps> = (props) => {
        return (
            <Draggable
                handle={`#${handleId}`}
                cancel={CANCEL_SELECTOR}
                nodeRef={paperRef as React.RefObject<HTMLElement>}
                defaultPosition={defaultPosition}
                onStop={onDragStop}
            >
                <Paper ref={paperRef} {...props} />
            </Draggable>
        );
    };
    return DraggablePaperComponent;
}

const DetachablePanel: React.FC<DetachablePanelProps> = ({
    open,
    onClose,
    title,
    subtitle,
    headerActions,
    children,
    panelId,
    width = { xs: '95vw', sm: '85vw', md: '78vw', lg: '70vw' },
    height = { xs: '90vh', sm: '85vh', md: '80vh' },
    defaultPosition,
}) => {
    const dragHandleId = React.useRef(`detachable-panel-${_instanceCounter++}`).current;
    const paperRef = React.useRef<HTMLDivElement>(null);

    const handleDragStop: DraggableEventHandler = React.useCallback((_e, data) => {
        savePanelStorage(panelId, { x: data.x, y: data.y });
    }, [panelId]);

    const DraggablePaper = React.useRef(
        makeDraggablePaper(dragHandleId, panelId, paperRef, handleDragStop, defaultPosition)
    ).current;

    // Read stored size once on mount; update on resize via ResizeObserver
    const stored = React.useMemo(() => loadPanelStorage(panelId), [panelId]);
    const sizeOverride = stored.width != null && stored.height != null
        ? { width: stored.width, height: stored.height }
        : {};

    // Debounce ref for resize writes
    const resizeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // Defer to after Dialog/Portal has committed the Paper to the DOM,
    // then observe with border-box dimensions (offsetWidth/Height) so that
    // save → restore is stable (no progressive shrinkage from contentRect drift).
    React.useEffect(() => {
        if (!open) return;
        let ro: ResizeObserver;
        const raf = requestAnimationFrame(() => {
            const el = paperRef.current;
            if (!el) return;
            ro = new ResizeObserver(entries => {
                for (const entry of entries) {
                    const target = entry.target as HTMLElement;
                    const w = target.offsetWidth;
                    const h = target.offsetHeight;
                    if (w > 0 && h > 0) {
                        if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
                        resizeTimerRef.current = setTimeout(() => {
                            savePanelStorage(panelId, { width: w, height: h });
                        }, 300);
                    }
                }
            });
            ro.observe(el);
        });
        return () => {
            cancelAnimationFrame(raf);
            ro?.disconnect();
            if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
        };
    }, [open, panelId]);

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
            maxWidth={false}
            hideBackdrop
            disableEnforceFocus
            disableAutoFocus
            disableScrollLock
            sx={{ pointerEvents: 'none' }}
            PaperComponent={DraggablePaper}
            slotProps={{
                paper: {
                    elevation: 2,
                    sx: {
                        pointerEvents: 'auto',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        backgroundColor: (theme) => theme.palette.mode === 'light' ? grey[50] : grey[800],
                        width,
                        height,
                        maxHeight: 'none',
                        ...sizeOverride,
                        minWidth: 200,
                        minHeight: 150,
                        resize: 'both',
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        p: 1,
                    }
                }
            }}
        >
            <DialogTitle
                id={dragHandleId}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 0.5,
                    px: 1,
                    cursor: 'move',
                    userSelect: 'none',
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
                    {subtitle && (
                        <Typography variant="caption" color="text.disabled">{subtitle}</Typography>
                    )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    {headerActions}
                    <IconButton size="small" onClick={onClose} aria-label="Close panel">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {children}
            </Box>
        </Dialog>
    );
};

export default DetachablePanel;
